import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { Shield, Trash2, Plus, Loader2, Building2, Pencil, RefreshCw, Copy, Eye, EyeOff } from "lucide-react";

function genPassword() {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%";
  return Array.from({ length: 14 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

function PwdField({ value, onChange, placeholder, label }) {
  const [show, setShow] = useState(false);
  const gen = () => { const p = genPassword(); onChange(p); setShow(true); };
  const copy = () => { navigator.clipboard.writeText(value); toast.success("Mot de passe copié"); };
  return (
    <div>
      {label && <Label className="text-xs">{label}</Label>}
      <div className="mt-1 flex items-center gap-1.5">
        <div className="relative flex-1">
          <Input
            type={show ? "text" : "password"}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-sm pr-8 font-mono text-sm"
            placeholder={placeholder || "••••••••"}
          />
          <button type="button" onClick={() => setShow((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
        <button type="button" title="Générer" onClick={gen}
          className="rounded-sm border border-zinc-200 px-2 py-2 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800 transition-colors">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
        {value && (
          <button type="button" title="Copier" onClick={copy}
            className="rounded-sm border border-zinc-200 px-2 py-2 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800 transition-colors">
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
import { toast } from "sonner";

const PERM_LABELS = {
  can_manage_rooms:      "Gestion des chambres",
  can_edit_sip_password: "Modifier mdp SIP",
  can_sync_phones:       "Synchro statut téléphones",
  can_provision:         "Provisioning téléphones",
  can_manage_subusers:   "Gestion sous-utilisateurs",
  can_manage_speed_dials:"Touches type 9",
  can_view_cdr:          "Voir appels CDR",
  can_edit_trunks:       "Trunk 3CX",
  can_view_config:       "Onglet Configuration",
  can_view_emergency:    "Urgences",
  can_edit_network:      "Réseau (WireGuard)",
  can_view_scripts:      "Scripts MikroTik / Debian",
  can_edit_vlan:         "Modifier VLAN",
};

const DEFAULT_FORM_DSI = {
  email: "", name: "", password: "",
  hotel_ids: [],
  permissions: {
    can_manage_rooms: true, can_edit_sip_password: true, can_sync_phones: true,
    can_provision: true, can_manage_subusers: true, can_manage_speed_dials: true,
    can_view_cdr: true, can_edit_trunks: false, can_view_config: false,
    can_view_emergency: false, can_edit_network: false, can_view_scripts: false,
    can_edit_vlan: false,
  },
};

export default function Admins() {
  const { user: me } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [dsiList, setDsiList] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [creating, setCreating] = useState(false);
  const [dsiDialog, setDsiDialog] = useState(false);
  const [editDsi, setEditDsi] = useState(null);
  const [dsiForm, setDsiForm] = useState(DEFAULT_FORM_DSI);
  const [savingDsi, setSavingDsi] = useState(false);

  const loadAdmins = () =>
    api.get("/super-admins").then((r) => setAdmins(r.data));
  const loadDsi = () =>
    api.get("/admins/dsi").then((r) => setDsiList(r.data));
  const loadHotels = () =>
    api.get("/hotels").then((r) => setHotels(r.data));

  useEffect(() => {
    Promise.all([loadAdmins(), loadDsi(), loadHotels()]).finally(() => setLoading(false));
  }, []);

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/super-admins", form);
      toast.success("Super admin créé");
      setForm({ email: "", name: "", password: "" });
      loadAdmins();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAdmin = async (id, email) => {
    if (!confirm(`Supprimer ${email} ?`)) return;
    try {
      await api.delete(`/super-admins/${id}`);
      toast.success("Super admin supprimé");
      setAdmins((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openCreateDsi = () => {
    setEditDsi(null);
    setDsiForm(DEFAULT_FORM_DSI);
    setDsiDialog(true);
  };

  const openEditDsi = (dsi) => {
    setEditDsi(dsi);
    setDsiForm({
      email: dsi.email,
      name: dsi.name,
      password: "",
      hotel_ids: dsi.hotel_ids || [],
      permissions: { ...DEFAULT_FORM_DSI.permissions, ...dsi.permissions },
    });
    setDsiDialog(true);
  };

  const saveDsi = async () => {
    setSavingDsi(true);
    try {
      if (editDsi) {
        await api.put(`/admins/dsi/${editDsi.id}`, {
          name: dsiForm.name,
          hotel_ids: dsiForm.hotel_ids,
          permissions: dsiForm.permissions,
        });
        toast.success("DSI mis à jour");
      } else {
        await api.post("/admins/dsi", dsiForm);
        toast.success("DSI créé");
      }
      setDsiDialog(false);
      loadDsi();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingDsi(false);
    }
  };

  const deleteDsi = async (id, email) => {
    if (!confirm(`Supprimer le DSI ${email} et tous ses sous-utilisateurs ?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("DSI supprimé");
      loadDsi();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleHotel = (id) =>
    setDsiForm((f) => ({
      ...f,
      hotel_ids: f.hotel_ids.includes(id)
        ? f.hotel_ids.filter((h) => h !== id)
        : [...f.hotel_ids, id],
    }));

  const togglePerm = (key) =>
    setDsiForm((f) => ({
      ...f,
      permissions: { ...f.permissions, [key]: !f.permissions[key] },
    }));

  if (loading) return <div className="font-mono text-sm text-zinc-400">Chargement…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-zinc-600" />
        <h1 className="text-2xl font-bold text-zinc-900">Administration</h1>
      </div>

      {/* Super admins */}
      <Card>
        <CardHeader><CardTitle>Super Admins</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-zinc-500">
                <th className="pb-2 pr-4">Nom</th>
                <th className="pb-2 pr-4">Email</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-medium text-zinc-800">{a.name}</td>
                  <td className="py-2 pr-4 font-mono text-[13px] text-zinc-500">{a.email}</td>
                  <td className="py-2 text-right">
                    {a.id !== me?.id ? (
                      <button onClick={() => handleDeleteAdmin(a.id, a.email)}
                        className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    ) : (
                      <span className="text-[11px] font-mono text-blue-500">vous</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <form onSubmit={handleCreateAdmin} className="mt-4 grid grid-cols-3 gap-4 border-t pt-4">
            <div className="space-y-1">
              <Label>Nom</Label>
              <Input placeholder="Dylan Delver" value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Email *</Label>
              <Input type="email" required placeholder="admin@example.com" value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <PwdField
                label="Mot de passe *"
                value={form.password}
                onChange={(v) => setForm((f) => ({ ...f, password: v }))}
                placeholder="Requis"
              />
            </div>
            <div className="col-span-3 flex justify-end">
              <Button type="submit" disabled={creating}>
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Créer
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* DSI / hotel admins */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>DSI / Hotel Admins</CardTitle>
            <Button onClick={openCreateDsi} className="rounded-sm bg-blue-600 hover:bg-blue-700">
              <Plus className="mr-1 h-4 w-4" /> Nouveau DSI
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {dsiList.length === 0 ? (
            <p className="text-sm text-zinc-400">Aucun DSI configuré.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-zinc-500">
                  <th className="pb-2 pr-4">Nom</th>
                  <th className="pb-2 pr-4">Email</th>
                  <th className="pb-2 pr-4">Hôtels</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {dsiList.map((d) => (
                  <tr key={d.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium text-zinc-800">{d.name}</td>
                    <td className="py-2 pr-4 font-mono text-[13px] text-zinc-500">{d.email}</td>
                    <td className="py-2 pr-4 text-xs text-zinc-500">
                      {(d.hotel_ids || []).length} hôtel(s)
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEditDsi(d)}
                          className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => deleteDsi(d.id, d.email)}
                          className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Dialog créer / éditer DSI */}
      <Dialog open={dsiDialog} onOpenChange={(o) => !o && setDsiDialog(false)}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight">
              {editDsi ? `Modifier ${editDsi.name}` : "Nouveau DSI"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nom</Label>
                <Input value={dsiForm.name} onChange={(e) => setDsiForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1 rounded-sm text-sm" placeholder="Jean Dupont" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={dsiForm.email}
                  onChange={(e) => setDsiForm((f) => ({ ...f, email: e.target.value }))}
                  disabled={!!editDsi}
                  className="mt-1 rounded-sm text-sm disabled:bg-zinc-50 disabled:text-zinc-400"
                  placeholder="dsi@hotel.fr" />
              </div>
              <div className="col-span-2">
                <PwdField
                  label={editDsi ? "Nouveau mot de passe (laisser vide pour ne pas changer)" : "Mot de passe"}
                  value={dsiForm.password}
                  onChange={(v) => setDsiForm((f) => ({ ...f, password: v }))}
                  placeholder={editDsi ? "••••••••" : "Requis"}
                />
              </div>
            </div>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
                Hôtels assignés
              </p>
              <div className="flex flex-wrap gap-2">
                {hotels.map((h) => (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => toggleHotel(h.id)}
                    className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-xs font-medium transition-colors ${
                      dsiForm.hotel_ids.includes(h.id)
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-zinc-200 text-zinc-500 hover:border-zinc-400"
                    }`}
                  >
                    <Building2 className="h-3 w-3" /> {h.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
                Permissions
              </p>
              <div className="space-y-2">
                {Object.entries(PERM_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between py-1">
                    <span className="text-sm text-zinc-700">{label}</span>
                    <Switch
                      checked={!!dsiForm.permissions[key]}
                      onCheckedChange={() => togglePerm(key)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDsiDialog(false)}>
              Annuler
            </Button>
            <Button className="rounded-sm bg-blue-600 hover:bg-blue-700" onClick={saveDsi} disabled={savingDsi}>
              {savingDsi && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editDsi ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
